import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { isNil } from 'ramda'
import { DisplayTable } from '@/features/Application/elements/tables'
import {
  LabeledValue,
  LabelText,
  ValueText,
  PhoneLink,
  EmailLink,
  Wrapper,
  EmptyComponent,
  BasicSubTitle
} from '@/features/Application/elements'

const EmploymentReferencesView = ({ application, isLocked, applicationLoading }) => {
  const { t } = useTranslation()
  const employers = application.user_employers ?? []
  const title = t('l.application.view.employment_references.label', 'Employment References')

  const data = useMemo(
    () =>
      employers.map(employer => ({
        subTitle: <BasicSubTitle user={employer} isLocked={isLocked} />,
        items: [
          {
            id: 'Phone Number',
            cell: (
              <LabeledValue
                key="Phone Number"
                label={
                  <LabelText>
                    {t('l.application.view.phone_number.label', 'Phone Number')}
                  </LabelText>
                }
                value={
                  <PhoneLink
                    phoneNumber={employer.phone_number}
                    countryCode={employer.phone_country_code_alpha}
                  />
                }
              />
            )
          },
          {
            id: 'Email',
            cell: (
              <LabeledValue
                key="Email"
                label={<LabelText>{t('l.application.view.email.label', 'Email')}</LabelText>}
                value={
                  !isNil(employer.contact_email) ? (
                    <EmailLink email={employer.contact_email} />
                  ) : (
                    '-'
                  )
                }
              />
            )
          },
          {
            id: 'Company',
            cell: (
              <LabeledValue
                key="Company"
                label={<LabelText>{t('l.application.view.company.label', 'Company')}</LabelText>}
                value={<ValueText>{employer.employer_name}</ValueText>}
              />
            )
          },
          {
            id: 'Position',
            cell: (
              <LabeledValue
                key="Position"
                label={<LabelText>{t('l.application.view.position.label', 'Position')}</LabelText>}
                value={<ValueText>{employer.job_title}</ValueText>}
              />
            )
          },
          {
            id: 'Start Date',
            cell: (
              <LabeledValue
                key="Start Date"
                label={
                  <LabelText>{t('l.application.view.start_date.label', 'Start Date')}</LabelText>
                }
                value={
                  <ValueText>
                    {employer.start_date ? moment(employer.start_date).format('YYYY-MM-DD') : '-'}
                  </ValueText>
                }
              />
            )
          },
          {
            id: 'End Date',
            cell: (
              <LabeledValue
                key="End Date"
                label={<LabelText>{t('l.application.view.end_date.label', 'End Date')}</LabelText>}
                value={
                  <ValueText>
                    {employer.end_date
                      ? moment(employer.end_date).format('YYYY-MM-DD')
                      : t('r.profile.view.current_date.text', 'Current')}
                  </ValueText>
                }
              />
            )
          }
        ]
      })),
    [applicationLoading]
  )

  return (
    <Wrapper>
      <DisplayTable
        data={data}
        title={title}
        columns={1}
        subColumns={2}
        emptyComponent={
          <EmptyComponent isLoading={applicationLoading}>
            {t(
              'l.application.view.no_employment.label',
              'No employment references included in this application.'
            )}
          </EmptyComponent>
        }
      />
    </Wrapper>
  )
}

export default EmploymentReferencesView
